import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createPortal } from "react-dom";
import {
  StickyNote, ListChecks, Users as UsersIcon, Plus, Trash2, UserPlus,
  Check, Search, X, Send, CalendarClock,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import { Card, Button, Spinner } from "@/components/ui";
import { fmtDate } from "@/lib/dates";

// =============================================================================
// ProjectNotepadCard — T-150
//
// Three private/shared scratch surfaces for each project, sitting at the top
// of the Overview tab:
//
//   • Team        — shared free-text pad. All internal roles (director,
//                   manager, supervisor, field) read + write.
//   • My notes    — private free-text. Strictly per-user via RLS.
//   • My to-dos   — private checklist. Click to tick off. Optional
//                   "assign to someone" — creates the todo on their list
//                   AND queues an in_app notification (bell).
//
// Subcontractors don't see this card at all (subbie auth/onboarding isn't
// wired yet). When they're added, soften the role gate here + in migration 084.
//
// Persistence pattern:
//   • Notes textareas autosave 800ms after last keystroke (and on blur).
//     "Saved · 14:02" indicator shows fresh saves; flips to "Saving…" while
//     the mutation is in flight.
//   • Todos are optimistic: click checkbox → flips immediately, mutation
//     in background, rolls back on error.
// =============================================================================

interface Props {
  projectId: string;
  projectCode: string;
}

type TabKey = "team" | "personal" | "todos";

interface TodoRow {
  todo_id:            string;
  project_id:         string;
  owner_user_id:      string;
  body:               string;
  done:               boolean;
  created_by_user_id: string | null;
  created_at:         string;
  completed_at:       string | null;
  sort_idx:           number;
  due_date:           string | null;   // T-150 ph2 — ISO date (YYYY-MM-DD) or null
  // Embedded creator name (for "from <name>" tag on assigned todos)
  created_by:         { user_id: string; employees: { first_name: string; last_name: string } | null } | null;
  // Embedded owner — used to render "→ Joshua" on todos I assigned to others.
  owner:              { user_id: string; employees: { first_name: string; last_name: string } | null } | null;
}

// RAG bucket for a due date relative to today (local).
// Returns one of: "overdue" | "soon" | "later" | "none".
function dueBucket(due: string | null): "overdue" | "soon" | "later" | "none" {
  if (!due) return "none";
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(due + "T00:00:00");
  const diffDays = Math.floor((d.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays <= 0) return "overdue"; // due today or already past
  if (diffDays <= 2) return "soon";
  return "later";
}

function dueLabel(due: string | null): string {
  if (!due) return "";
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(due + "T00:00:00");
  const diffDays = Math.floor((d.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays === 0)  return "due today";
  if (diffDays === 1)  return "due tomorrow";
  if (diffDays === -1) return "1 day overdue";
  if (diffDays < 0)    return `${-diffDays} days overdue`;
  if (diffDays <= 6)   return `due in ${diffDays} days`;
  // For dates further out, show the date itself
  return `due ${d.toLocaleDateString(undefined, { day: "2-digit", month: "short" })}`;
}

interface InternalUser {
  user_id:    string;
  first_name: string;
  last_name:  string;
  role_name:  string;
}

// -----------------------------------------------------------------------------

export default function ProjectNotepadCard({ projectId, projectCode }: Props) {
  const user = useRequireAuth();

  // Subbies don't get the notepad (yet).
  if (!user || !hasRole(user, "field")) return null;

  const [tab, setTab] = useState<TabKey>("todos");

  return (
    <Card>
      <div className="p-4">
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <StickyNote size={14} className="text-text-muted"/>
            <span className="text-[12px] uppercase tracking-wider text-text-muted">
              Working notes &amp; to-dos
            </span>
          </div>
          <span className="text-[10px] text-text-dim italic hidden sm:inline">
            scratchpad — not the official project notes
          </span>
        </div>

        {/* Tab strip */}
        <div className="flex items-center gap-1 mb-3 border-b border-line/60">
          <TabBtn active={tab === "todos"}    onClick={() => setTab("todos")}    icon={ListChecks}>My to-dos</TabBtn>
          <TabBtn active={tab === "personal"} onClick={() => setTab("personal")} icon={StickyNote}>My notes</TabBtn>
          <TabBtn active={tab === "team"}     onClick={() => setTab("team")}     icon={UsersIcon}>Team</TabBtn>
        </div>

        {tab === "todos"    && <TodosPanel    projectId={projectId} projectCode={projectCode} userId={user.user_id} />}
        {tab === "personal" && <PersonalNotePanel projectId={projectId} userId={user.user_id} />}
        {tab === "team"     && <TeamNotePanel projectId={projectId} />}
      </div>
    </Card>
  );
}

function TabBtn({ active, onClick, icon: Icon, children }: {
  active: boolean; onClick: () => void; icon: any; children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 text-[12px] font-medium border-b-2 -mb-px inline-flex items-center gap-1.5 transition-colors ${
        active ? "border-accent text-text" : "border-transparent text-text-muted hover:text-text"
      }`}
    >
      <Icon size={12}/>
      {children}
    </button>
  );
}

// =============================================================================
// Notes panels — autosave debounced textareas
// =============================================================================

function PersonalNotePanel({ projectId, userId }: { projectId: string; userId: string }) {
  return (
    <NoteEditor
      queryKey={["project_user_note", projectId, userId]}
      load={async () => {
        const { data, error } = await supabase
          .from("project_user_notes")
          .select("body, updated_at")
          .eq("project_id", projectId)
          .eq("user_id", userId)
          .maybeSingle();
        if (error) throw error;
        return data ?? { body: "", updated_at: null };
      }}
      save={async (body) => {
        const { error } = await supabase
          .from("project_user_notes")
          .upsert({ project_id: projectId, user_id: userId, body }, { onConflict: "project_id,user_id" });
        if (error) throw error;
      }}
      placeholder="Just for you. Followups, questions for Brett, things to chase, half-formed thoughts. Nobody else can see this — not even directors."
    />
  );
}

function TeamNotePanel({ projectId }: { projectId: string }) {
  return (
    <NoteEditor
      queryKey={["project_team_note", projectId]}
      showUpdatedBy
      load={async () => {
        const { data, error } = await supabase
          .from("project_team_notes")
          .select("body, updated_at, updated_by, users:updated_by (employees:employee_id (first_name, last_name))")
          .eq("project_id", projectId)
          .maybeSingle();
        if (error) throw error;
        return data ?? { body: "", updated_at: null, users: null };
      }}
      save={async (body) => {
        const { error } = await supabase
          .from("project_team_notes")
          .upsert({ project_id: projectId, body }, { onConflict: "project_id" });
        if (error) throw error;
      }}
      placeholder="Shared with the internal team. Use this for ad-hoc working notes everyone should see — site quirks, client preferences, current blockers."
    />
  );
}

function NoteEditor({
  queryKey, load, save, placeholder, showUpdatedBy = false,
}: {
  queryKey: any[];
  load: () => Promise<any>;
  save: (body: string) => Promise<void>;
  placeholder: string;
  showUpdatedBy?: boolean;
}) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey, queryFn: load });
  const [body, setBody] = useState("");
  const [hydrated, setHydrated] = useState(false);
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const debounceRef = useRef<number | null>(null);

  // Hydrate local state once data arrives
  useEffect(() => {
    if (!hydrated && data) {
      setBody(data.body ?? "");
      if (data.updated_at) setSavedAt(new Date(data.updated_at));
      setHydrated(true);
    }
  }, [data, hydrated]);

  const mut = useMutation({
    mutationFn: save,
    onSuccess: () => {
      setSavedAt(new Date());
      qc.invalidateQueries({ queryKey });
    },
  });

  function scheduleSave(next: string) {
    setBody(next);
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => {
      mut.mutate(next);
    }, 800);
  }

  function flushSave() {
    if (debounceRef.current) {
      window.clearTimeout(debounceRef.current);
      debounceRef.current = null;
      mut.mutate(body);
    }
  }

  if (isLoading) return <div className="flex justify-center py-4"><Spinner size={14}/></div>;

  const updatedByName = showUpdatedBy && data?.users?.employees
    ? `${data.users.employees.first_name} ${data.users.employees.last_name}`
    : null;

  return (
    <div className="space-y-2">
      <textarea
        value={body}
        onChange={(e) => scheduleSave(e.target.value)}
        onBlur={flushSave}
        placeholder={placeholder}
        rows={6}
        className="w-full bg-ink border border-line rounded-md px-3 py-2 text-[13px] text-text resize-y min-h-[100px] focus:outline-none focus:border-accent placeholder:text-text-dim/70"
      />
      <div className="flex items-center justify-between text-[10px] text-text-dim">
        <span>
          {mut.isPending
            ? "Saving…"
            : savedAt
              ? `Saved · ${savedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`
              : ""}
        </span>
        {updatedByName && (
          <span>Last edit: {updatedByName}</span>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// Todos panel
// =============================================================================

function TodosPanel({ projectId, projectCode, userId }: {
  projectId: string; projectCode: string; userId: string;
}) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState("");
  const [assignTo, setAssignTo] = useState<InternalUser | null>(null);
  const [dueDate, setDueDate] = useState<string>(""); // YYYY-MM-DD or ""
  const [lastSentTo, setLastSentTo] = useState<string | null>(null); // brief send confirmation

  const { data: todos, isLoading } = useQuery({
    queryKey: ["project_user_todos", projectId, userId],
    queryFn: async () => {
      // Mig 088: widen to todos I own OR I created (so I can track items
      // I delegated to teammates). Splitting happens in render below.
      const { data, error } = await supabase
        .from("project_user_todos")
        .select(`
          todo_id, project_id, owner_user_id, body, done,
          created_by_user_id, created_at, completed_at, sort_idx, due_date,
          created_by:created_by_user_id (
            user_id,
            employees:employee_id (first_name, last_name)
          ),
          owner:owner_user_id (
            user_id,
            employees:employee_id (first_name, last_name)
          )
        `)
        .eq("project_id", projectId)
        .or(`owner_user_id.eq.${userId},created_by_user_id.eq.${userId}`)
        .order("done", { ascending: true })
        // Open todos: nulls last on due_date so "due soon" floats to the top.
        .order("due_date", { ascending: true, nullsFirst: false })
        .order("sort_idx", { ascending: true })
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as TodoRow[];
    },
  });

  const addMut = useMutation({
    mutationFn: async () => {
      const text = draft.trim();
      if (!text) return;
      // T-162a — RPC param is typed `string | undefined`; "" → undefined (parameter omitted).
      const due = dueDate || undefined;
      if (assignTo && assignTo.user_id !== userId) {
        // Assignment path — RPC inserts row + queues notification.
        const { error } = await supabase.schema("app").rpc("assign_project_todo", {
          p_project_id: projectId,
          p_owner_user: assignTo.user_id,
          p_body:       text,
          p_due_date:   due,
        });
        if (error) throw error;
      } else {
        // Self-add — straight INSERT.
        const { error } = await supabase
          .from("project_user_todos")
          .insert({
            project_id:         projectId,
            owner_user_id:      userId,
            created_by_user_id: userId,
            body:               text,
            due_date:           due,
          });
        if (error) throw error;
      }
    },
    onSuccess: (_data, _vars) => {
      // Brief inline confirmation when an assignment goes through, so it's
      // clear something happened (the todo itself disappears from your list
      // because ownership transferred).
      if (assignTo && assignTo.user_id !== userId) {
        setLastSentTo(`${assignTo.first_name} ${assignTo.last_name}`);
        // Auto-clear after 4s
        window.setTimeout(() => setLastSentTo(null), 4000);
      }
      setDraft("");
      setAssignTo(null);
      setDueDate("");
      qc.invalidateQueries({ queryKey: ["project_user_todos", projectId, userId] });
    },
  });

  // Per-row due-date editor — used to set/clear the date after a todo exists.
  const updateDueMut = useMutation({
    mutationFn: async ({ todoId, due }: { todoId: string; due: string | null }) => {
      const { error } = await supabase
        .from("project_user_todos")
        .update({ due_date: due })
        .eq("todo_id", todoId);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["project_user_todos", projectId, userId] });
    },
  });

  const toggleMut = useMutation({
    mutationFn: async ({ todoId, done }: { todoId: string; done: boolean }) => {
      const { error } = await supabase
        .from("project_user_todos")
        .update({ done })
        .eq("todo_id", todoId);
      if (error) throw error;
    },
    onMutate: async ({ todoId, done }) => {
      await qc.cancelQueries({ queryKey: ["project_user_todos", projectId, userId] });
      const prev = qc.getQueryData<TodoRow[]>(["project_user_todos", projectId, userId]);
      qc.setQueryData<TodoRow[]>(["project_user_todos", projectId, userId], (old) =>
        (old ?? []).map(t => t.todo_id === todoId ? { ...t, done } : t)
      );
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(["project_user_todos", projectId, userId], ctx.prev);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["project_user_todos", projectId, userId] });
    },
  });

  const deleteMut = useMutation({
    mutationFn: async (todoId: string) => {
      const { error } = await supabase
        .from("project_user_todos")
        .delete()
        .eq("todo_id", todoId);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["project_user_todos", projectId, userId] });
    },
  });

  // Split into three buckets:
  //   • mineOpen     — owned by me, not done            (interactive)
  //   • mineDone     — owned by me, done                (in expander)
  //   • tracking     — created by me, owned by someone else, not done (read-only)
  // Tracking-completed (assigned + done) folds under the same expander as mineDone
  // — the reasonable thing to track is "what's outstanding", not all history.
  const all       = todos ?? [];
  const mineOpen  = all.filter(t => t.owner_user_id === userId && !t.done);
  const mineDone  = all.filter(t => t.owner_user_id === userId &&  t.done);
  const tracking  = all.filter(t => t.owner_user_id !== userId && t.created_by_user_id === userId && !t.done);
  const trackingDone = all.filter(t => t.owner_user_id !== userId && t.created_by_user_id === userId && t.done);
  // Back-compat aliases used a few times below.
  const open      = mineOpen;
  const completed = [...mineDone, ...trackingDone];

  return (
    <div className="space-y-3">
      {/* Add row */}
      <div className="flex items-center gap-2">
        <div className="flex-1 flex items-center gap-2 bg-ink border border-line rounded-md px-3 py-2 focus-within:border-accent">
          <Plus size={13} className="text-text-muted flex-shrink-0"/>
          <input
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && draft.trim()) {
                e.preventDefault();
                addMut.mutate();
              }
            }}
            placeholder={assignTo
              ? `Add a to-do for ${assignTo.first_name} ${assignTo.last_name}…`
              : "Add a to-do for yourself…"
            }
            className="flex-1 bg-transparent text-[13px] text-text placeholder:text-text-dim/70 focus:outline-none"
          />
          {assignTo && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-accent/15 text-accent text-[10px] font-medium">
              <UsersIcon size={10}/>
              {assignTo.first_name}
              <button onClick={() => setAssignTo(null)} className="hover:bg-accent/20 rounded">
                <X size={10}/>
              </button>
            </span>
          )}
          {dueDate && (
            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${
              dueBucket(dueDate) === "overdue" ? "bg-lost/15 text-lost"
                : dueBucket(dueDate) === "soon" ? "bg-amber-500/15 text-amber-500"
                : "bg-ok/15 text-ok"
            }`}>
              <CalendarClock size={10}/>
              {dueLabel(dueDate)}
              <button onClick={() => setDueDate("")} className="hover:bg-current/20 rounded">
                <X size={10}/>
              </button>
            </span>
          )}
          <DueDatePickerBtn value={dueDate} onChange={setDueDate} />
          <AssigneePicker selected={assignTo} onSelect={setAssignTo} currentUserId={userId} />
        </div>
        <Button
          size="sm"
          variant="primary"
          icon={assignTo ? Send : Plus}
          onClick={() => draft.trim() && addMut.mutate()}
          disabled={!draft.trim() || addMut.isPending}
        >
          {assignTo ? "Send" : "Add"}
        </Button>
      </div>

      {addMut.isError && (
        <div className="text-[11px] text-lost">
          Couldn't save: {(addMut.error as any)?.message ?? "unknown error"}
        </div>
      )}

      {/* Brief send confirmation — auto-clears after 4s */}
      {lastSentTo && (
        <div className="text-[11px] text-ok bg-ok/10 border border-ok/20 rounded-md px-2.5 py-1.5 inline-flex items-center gap-1.5">
          <Check size={12}/> Sent to {lastSentTo} — they'll see it in their list and get a bell notification.
        </div>
      )}

      {/* Lists */}
      {isLoading && <div className="flex justify-center py-3"><Spinner size={14}/></div>}

      {!isLoading && open.length === 0 && completed.length === 0 && tracking.length === 0 && (
        <div className="text-[12px] text-text-muted py-2 italic">
          No to-dos yet. Jot down what you're chasing on this project — only you can see your list.
        </div>
      )}

      {/* MY OPEN TODOS — owned by me, interactive */}
      {open.length > 0 && (
        <div className="space-y-1">
          {open.map(t => (
            <TodoRowEl
              key={t.todo_id}
              todo={t}
              onToggle={(done) => toggleMut.mutate({ todoId: t.todo_id, done })}
              onDelete={() => deleteMut.mutate(t.todo_id)}
              onUpdateDue={(due) => updateDueMut.mutate({ todoId: t.todo_id, due })}
              ownByMe={t.owner_user_id === userId}
            />
          ))}
        </div>
      )}

      {/* TRACKING — todos I assigned to others, read-only with assignee tag.
          Only the owner can tick them off; deleting is allowed (lets you
          withdraw a wrongly-assigned todo). */}
      {tracking.length > 0 && (
        <div className="pt-2 mt-2 border-t border-line/40">
          <div className="text-[10px] uppercase tracking-wider text-text-muted mb-1.5 inline-flex items-center gap-1.5">
            <UsersIcon size={10}/> Tracking — assigned by you
          </div>
          <div className="space-y-1">
            {tracking.map(t => (
              <TodoRowEl
                key={t.todo_id}
                todo={t}
                onToggle={() => { /* no-op — only owner can tick */ }}
                onDelete={() => deleteMut.mutate(t.todo_id)}
                ownByMe={false}
                readonly
              />
            ))}
          </div>
        </div>
      )}

      {completed.length > 0 && (
        <details className="text-[11px] text-text-muted">
          <summary className="cursor-pointer hover:text-text select-none">
            {completed.length} completed
          </summary>
          <div className="mt-1.5 space-y-1">
            {completed.map(t => {
              const ownByMe = t.owner_user_id === userId;
              return (
                <TodoRowEl
                  key={t.todo_id}
                  todo={t}
                  onToggle={(done) => toggleMut.mutate({ todoId: t.todo_id, done })}
                  onDelete={() => deleteMut.mutate(t.todo_id)}
                  ownByMe={ownByMe}
                  readonly={!ownByMe}
                  completed
                />
              );
            })}
          </div>
        </details>
      )}

      <p className="text-[10px] text-text-dim italic pt-1">
        {projectCode} · only you can see your to-dos. Assigning a to-do sends it to that person's list and notifies them.
      </p>
    </div>
  );
}

function TodoRowEl({ todo, onToggle, onDelete, onUpdateDue, ownByMe, completed = false, readonly = false }: {
  todo: TodoRow;
  onToggle: (done: boolean) => void;
  onDelete: () => void;
  onUpdateDue?: (due: string | null) => void;
  ownByMe: boolean;
  completed?: boolean;
  readonly?: boolean;       // tracking mode — I created but assigned away
}) {
  const creatorName = todo.created_by?.employees
    ? `${todo.created_by.employees.first_name} ${todo.created_by.employees.last_name}`
    : null;
  const ownerName = todo.owner?.employees
    ? `${todo.owner.employees.first_name} ${todo.owner.employees.last_name}`
    : null;
  const wasAssignedToMe = ownByMe && todo.created_by_user_id !== todo.owner_user_id && creatorName;
  const bucket = dueBucket(todo.due_date);

  // RAG dot — sits at the very left of the row, before the checkbox.
  const dotColor =
    bucket === "overdue" ? "bg-lost"
      : bucket === "soon"   ? "bg-amber-500"
      : bucket === "later"  ? "bg-ok"
      : "bg-transparent";

  return (
    <div className={`group flex items-start gap-2 px-2 py-1.5 rounded hover:bg-ink-raised/40 ${
      completed ? "opacity-55" : ""
    }`}>
      {/* RAG dot — only renders when there's a due date (none = transparent slot
          but width preserved so checkboxes still align across rows) */}
      <span
        className={`mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0 ${dotColor}`}
        title={bucket === "none" ? "" : dueLabel(todo.due_date)}
      />
      {/* Checkbox — disabled in readonly (tracking) mode. Render a subtle
          dim circle instead so the row still aligns with owned-by-me rows. */}
      {readonly ? (
        <span
          className={`mt-0.5 w-4 h-4 rounded border border-text-muted/30 flex items-center justify-center flex-shrink-0 ${
            todo.done ? "bg-text-muted/20" : ""
          }`}
          title="Only the assignee can tick this off"
        >
          {todo.done && <Check size={10} strokeWidth={3} className="text-text-muted"/>}
        </span>
      ) : (
        <button
          onClick={() => onToggle(!todo.done)}
          className={`mt-0.5 w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition ${
            todo.done
              ? "bg-accent border-accent text-ink"
              : "border-text-muted/60 hover:border-accent"
          }`}
          title={todo.done ? "Mark as not done" : "Mark as done"}
        >
          {todo.done && <Check size={10} strokeWidth={3} />}
        </button>
      )}
      <div className="flex-1 min-w-0">
        <div className={`text-[13px] text-text ${todo.done ? "line-through text-text-muted" : ""}`}>
          {todo.body}
        </div>
        <div className="text-[10px] text-text-dim flex items-center gap-2 mt-0.5 flex-wrap">
          {wasAssignedToMe && (
            <span className="inline-flex items-center gap-1 text-accent">
              <UserPlus size={9}/> from {creatorName}
            </span>
          )}
          {readonly && ownerName && (
            <span className="inline-flex items-center gap-1 text-accent">
              <UserPlus size={9}/> assigned to {ownerName}
            </span>
          )}
          <span>{fmtDate(todo.created_at)}</span>
          {todo.completed_at && <span>· done {fmtDate(todo.completed_at)}</span>}
          {todo.due_date && !todo.done && (
            <span className={
              bucket === "overdue" ? "text-lost font-medium"
                : bucket === "soon"   ? "text-amber-500 font-medium"
                : "text-text-muted"
            }>
              · {dueLabel(todo.due_date)}
            </span>
          )}
        </div>
      </div>
      {/* Inline due-date editor — only on open todos for the owner. Native
          date input is plenty for this scratchpad scope; a fuller picker
          would be overkill. */}
      {!completed && !todo.done && !readonly && onUpdateDue && (
        <InlineDueEditor
          value={todo.due_date}
          onChange={(d) => onUpdateDue(d)}
        />
      )}
      <button
        onClick={onDelete}
        className="opacity-0 group-hover:opacity-100 transition p-1 rounded text-text-muted hover:text-lost hover:bg-lost/10"
        title="Delete"
      >
        <Trash2 size={11}/>
      </button>
    </div>
  );
}

// Tiny native-date inline picker. Click icon → opens a date input that, on
// change, fires onChange. "Clear" link drops the date back to null.
function InlineDueEditor({ value, onChange }: {
  value: string | null;
  onChange: (next: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        onClick={() => setOpen(v => !v)}
        className={`opacity-60 group-hover:opacity-100 transition p-1 rounded hover:bg-ink-raised ${
          value ? "text-accent" : "text-text-muted"
        }`}
        title={value ? `Due ${value} — click to change` : "Set a due date"}
      >
        <CalendarClock size={11}/>
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)}/>
          <div className="absolute right-0 top-full mt-1 z-50 bg-ink-card border border-line rounded-md shadow-xl p-2 flex items-center gap-2">
            <input
              type="date"
              value={value ?? ""}
              onChange={(e) => { onChange(e.target.value || null); setOpen(false); }}
              className="bg-ink border border-line rounded px-2 py-1 text-[12px] text-text focus:outline-none focus:border-accent"
            />
            {value && (
              <button
                onClick={() => { onChange(null); setOpen(false); }}
                className="text-[11px] text-text-muted hover:text-lost"
              >
                Clear
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// =============================================================================
// Due-date picker button (for the add-todo row)
// =============================================================================

function DueDatePickerBtn({ value, onChange }: {
  value: string;
  onChange: (next: string) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        onClick={() => setOpen(v => !v)}
        className={`p-1 rounded hover:bg-ink-raised transition ${
          value ? "text-accent" : "text-text-muted hover:text-accent"
        }`}
        title={value ? "Change due date" : "Add a due date"}
        type="button"
      >
        <CalendarClock size={13}/>
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)}/>
          <div className="absolute right-0 top-full mt-1 z-50 bg-ink-card border border-line rounded-md shadow-xl p-2 flex items-center gap-2">
            <input
              type="date"
              value={value}
              onChange={(e) => { onChange(e.target.value); setOpen(false); }}
              className="bg-ink border border-line rounded px-2 py-1 text-[12px] text-text focus:outline-none focus:border-accent"
            />
            {value && (
              <button
                type="button"
                onClick={() => { onChange(""); setOpen(false); }}
                className="text-[11px] text-text-muted hover:text-lost"
              >
                Clear
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// =============================================================================
// Assignee picker — portal-rendered, searchable employee list
// =============================================================================

function AssigneePicker({
  selected, onSelect, currentUserId,
}: {
  selected: InternalUser | null;
  onSelect: (u: InternalUser | null) => void;
  currentUserId: string;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const btnRef = useRef<HTMLButtonElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const { data: users } = useQuery({
    queryKey: ["internal_users_for_assignment"],
    enabled: open,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("users")
        .select("user_id, role_name, employees:employee_id (first_name, last_name)")
        .in("role_name", ["director", "manager", "supervisor", "field"]);
      if (error) throw error;
      return (data ?? [])
        .filter((u: any) => u.user_id !== currentUserId && u.employees)
        .map((u: any) => ({
          user_id:    u.user_id,
          first_name: u.employees.first_name,
          last_name:  u.employees.last_name,
          role_name:  u.role_name,
        })) as InternalUser[];
    },
  });

  function openPicker() {
    if (!btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    setPos({ top: r.bottom + 4, left: Math.max(8, r.right - 240) });
    setOpen(true);
  }

  const filtered = useMemo(() => {
    if (!users) return [];
    const ql = q.toLowerCase().trim();
    if (!ql) return users;
    return users.filter(u =>
      u.first_name.toLowerCase().includes(ql) ||
      u.last_name.toLowerCase().includes(ql)
    );
  }, [users, q]);

  return (
    <>
      <button
        ref={btnRef}
        onClick={open ? () => setOpen(false) : openPicker}
        className={`p-1 rounded hover:bg-ink-raised text-text-muted hover:text-accent transition ${
          selected ? "text-accent" : ""
        }`}
        title="Assign to someone"
      >
        <UserPlus size={13}/>
      </button>
      {open && pos && createPortal(
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)}/>
          <div
            className="fixed z-50 w-60 bg-ink-card border border-line rounded-md shadow-xl"
            style={{ top: pos.top, left: pos.left }}
          >
            <div className="flex items-center gap-1.5 border-b border-line px-2.5 py-2">
              <Search size={12} className="text-text-muted"/>
              <input
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Find a teammate…"
                className="flex-1 bg-transparent text-[12px] text-text focus:outline-none placeholder:text-text-dim/70"
              />
            </div>
            <div className="max-h-56 overflow-y-auto">
              {filtered.length === 0 && (
                <div className="px-3 py-3 text-[11px] text-text-muted italic">
                  No teammates match.
                </div>
              )}
              {filtered.map(u => (
                <button
                  key={u.user_id}
                  onClick={() => { onSelect(u); setOpen(false); setQ(""); }}
                  className="w-full text-left px-3 py-2 hover:bg-ink-raised flex items-center justify-between text-[12px]"
                >
                  <span>{u.first_name} {u.last_name}</span>
                  <span className="text-text-muted text-[10px] font-mono">{u.role_name}</span>
                </button>
              ))}
            </div>
          </div>
        </>,
        document.body
      )}
    </>
  );
}
